import {
  ApolloClient,
  ApolloLink,
  HttpLink,
  InMemoryCache,
  Operation,
} from "@apollo/client/core";
import { onError } from "@apollo/client/link/error";
import { addBreadcrumb, captureException } from "@sentry/browser";
import { TFunction } from "i18next";
import { toast } from "react-toastify";
import { getQueryStrings } from "../utils/url-util";
import { i18n } from "../utils/localize-utils";
import introspectionResult from "./introspection-result.json";

export const apolloClient = new ApolloClient({
  link: ApolloLink.from([
    new ApolloLink((operation, forward) => {
      captureGraphQLResult(operation);
      return forward(operation);
    }),
    new ApolloLink((operation: Operation, forward) => {
      operation.setContext(({ headers = {} }) => ({
        headers: {
          ...headers,
          "X-Graffer-GraphQL-Query": operation.operationName,
        },
      }));
      return forward(operation);
    }),
    onError(({ operation, graphQLErrors, networkError }) => {
      /**
       * GraphQLのエラーはHTTPステータスなど仕様に決まっていない事が多く、
       * サーバーの実装と密結合なのでサーバーのエラーハンドリングと相違がないようにする。
       *
       * accountsのサーバーサイドの実装ではGraphQLのResolverに到達したリクエストは200を返す。
       * networkError.statusCodeが存在するケースはResolverに到達していないものとなる。
       * graphQLErrorsにはResolverに到達したリクエストが予期せぬエラーを返した場合のエラーが入っている。
       * Expectedなエラーに関してはUnionTypeを使って明示的にハンドリングする。
       * ex. type PostImageResult = Image | SizeTooLarge | UnsupportedFormat ...
       */
      if (networkError) {
        const statusCode = getStatusCode(networkError);
        if (!statusCode) {
          captureException(networkError);
          captureGraphQLError(operation);
          showError("system.error");
        } else if (statusCode === 403) {
          showError("auth.timeout");
          const to = getQueryStrings().get("to");
          if (to) {
            window.location.href = to;
          }
        } else if (statusCode >= 400 && statusCode < 500) {
          showError("bad.request");
        } else {
          captureException(networkError);
          captureGraphQLError(operation);
          showError("system.error");
        }

        /**
         * NOTE: サーバーサイドの実装にもよるが、HTTPステータスが4xx/5xxでかつ
         * bodyにもGraphQLフォーマットのエラーが入ってくるケースがあるとエラートーストが
         * 多重に開いて見た目が良くないかも知れないのでここでreturnする。
         * ちゃんと実装方針が固まったらこのコメントを消す
         */
        return;
      }

      if (graphQLErrors) {
        graphQLErrors.forEach(({ message, extensions }) => {
          if (isConstraintDirectiveError(extensions)) {
            showConstraintDirectiveError(extensions);
            // https://github.com/graffer-inc/accounts/pull/595
          } else if (
            message === "Failed to exchange auth_code to access_token"
          ) {
            return;
          } else {
            showError(message);
          }
        });
      }
    }),
    new HttpLink({
      uri: () => "/api/accounts/graphql/v1/query?_=" + Date.now(),
      credentials: "same-origin",
    }),
  ]),
  cache: new InMemoryCache({
    possibleTypes: introspectionResult.possibleTypes,
  }),
});

// Error | ServerError | ServerParseError なnetworkErrorからHTTP statusCodeを取り出す
function getStatusCode(e: any): number | undefined {
  return e.statusCode;
}

function showError(code: string) {
  i18n.promise.then((t) => {
    toast.error(resolveErrorMsg(t, code));
  });
}

type ConstraintDirectiveError = {
  code: "constraint-directive-error";
  constraint: "minLength" | "maxLength" | "pattern" | "min" | "max";
  extra: {
    value: string | number;
    reference: string | number;
  };
};

function isConstraintDirectiveError(extensions?: {
  [key: string]: any;
}): extensions is ConstraintDirectiveError {
  return extensions?.code === "constraint-directive-error";
}

function showConstraintDirectiveError(e: ConstraintDirectiveError) {
  i18n.promise.then((t) => {
    switch (e.constraint) {
      default:
        // エラーごとの文字列リソースがないので一般的なやつを出しておく
        toast.error(t(`%%%入力内容に誤りがあります%%%`) as string);
    }
  });
}

function resolveErrorMsg(t: TFunction, code: string): string {
  switch (code) {
    case "auth.timeout":
      return t(`%%%一定時間が経過したためログアウトしました%%%`);
    case "bad credential":
    case "user.unauthorized":
      return t(`%%%メールアドレスまたはパスワードが間違っています%%%`);
    case "user.already.exist":
      return t(`%%%すでに登録されているメールアドレスです%%%`);
    case "tfa.token.expired":
      return t(
        `%%%二段階認証を設定するURLの期限が切れています。再度、二段階認証を設定する操作を行ってください%%%`,
      );
    case "tfa.token_not_found":
      return t(
        `%%%二段階認証を設定するURLが無効です。再度、二段階認証を設定する操作を行ってください%%%`,
      );
    case "otp.token.expired":
      return t(
        `%%%このワンタイムパスワードの期限は切れています。再度ログインを行なってください。%%%`,
      );
    case "otp.not_found":
      return t(
        `%%%このワンタイムパスワードは無効です。再度ログインを行なってください。%%%`,
      );
    case "email.already.used":
      return t(
        `%%%すでに登録されているメールアドレスです。Grafferアカウントでログインしてください。%%%`,
      );
    case "invalid.password":
      return t(`%%%入力された古いパスワードは正しくありません。%%%`);
    case "bad.request":
      return t(`%%%入力内容に誤りがあります%%%`);
    case "system.error":
    default:
      return t(
        `%%%システムエラーが発生しています。しばらく時間を置いて操作してください%%%`,
      );
  }
}

function captureGraphQLResult(operation: Operation) {
  addBreadcrumb({
    message: "GraphQL Operation: " + operation.operationName,
  });
}

function captureGraphQLError(operation: Operation) {
  addBreadcrumb({
    type: "error",
    level: "error",
    message: "GraphQL Operation: " + operation.operationName,
  });
}
